'use client';

import React, { useCallback } from 'react';
import { Spinner } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { EmptyContentIc } from '../../../../components/icons';
import { OffersTableProps, RevshareOffer } from '../types';
import { getTableColumns } from '../constants';
import OwnerCell from './OwnerCell';
import OfferDetailsCell from './OfferDetailsCell';
import StatisticsCell from './StatisticsCell';
import ActionsCell from './ActionsCell';

const OffersTable: React.FC<OffersTableProps> = ({
  offers,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onShowContent,
  onStartCampaign,
}) => {
  const { t, lang } = useTranslations();

  const renderCell = useCallback(
    (offer: RevshareOffer, columnKey: string) => {
      switch (columnKey) {
        case 'owner':
          return <OwnerCell offer={offer} />;
        case 'offerDetails':
          return <OfferDetailsCell offer={offer} />;
        case 'statistics':
          return <StatisticsCell offer={offer} />;
        case 'actions':
          return (
            <ActionsCell
              offer={offer}
              onShowContent={onShowContent}
              onStartCampaign={onStartCampaign}
            />
          );
        default:
          return null;
      }
    },
    [onShowContent, onStartCampaign]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner
          size="lg"
          classNames={{
            circle1: '!border-b-partner',
            circle2: '!border-b-partner/50',
          }}
        />
      </div>
    );
  }

  return (
    <UniversalTable
      key={lang}
      data={offers}
      columns={getTableColumns(t)}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('partner.revshare.offers.table.empty.title')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('partner.revshare.offers.table.empty.description')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default OffersTable;
